import { Button, FormField, Select, Textarea } from '@chopchop-se/ui';
import type { PurchaseOrderCreateInput } from '@warehouse/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ArticlePicker, SupplierPicker } from '../components/Pickers';
import { useUnitOptions, type ArticleOption } from '../master-data-port';

interface DraftLine {
  key: string;
  article: ArticleOption | null;
  qty: string;
  unitId: string | null;
}

function emptyLine(): DraftLine {
  return { key: `${Date.now()}-${Math.random().toString(16).slice(2)}`, article: null, qty: '', unitId: null };
}

/**
 * Manual purchase-order entry. Most POs come from Business Central (M8); this
 * covers everything BC does not know about — and the receiving flow does not
 * care which of the two produced the order.
 */
export function PurchaseOrderForm({
  onSubmit,
  onCancel,
  busy,
}: {
  onSubmit: (input: PurchaseOrderCreateInput) => void;
  onCancel: () => void;
  busy: boolean;
}): ReactNode {
  const { t } = useTranslation('receiving');
  const units = useUnitOptions();

  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [expectedDate, setExpectedDate] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [errors, setErrors] = useState<{ supplier?: string; lines?: string }>({});

  function patchLine(key: string, patch: Partial<DraftLine>): void {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  function submit(): void {
    const filled = lines.filter((line) => line.article && Number(line.qty) > 0);
    const nextErrors = {
      ...(supplierId ? {} : { supplier: t('po.form.supplierRequired') }),
      ...(filled.length > 0 ? {} : { lines: t('po.form.lineRequired') }),
    };
    setErrors(nextErrors);
    if (!supplierId || filled.length === 0) return;

    onSubmit({
      supplierId,
      expectedDate: expectedDate || null,
      note: note.trim() || null,
      lines: filled.map((line, index) => ({
        articleId: line.article!.id,
        qty: line.qty,
        unitId: line.unitId ?? line.article!.baseUnitId,
        sort: index,
      })),
    });
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <SupplierPicker
          value={supplierId}
          onChange={(supplier) => {
            setSupplierId(supplier?.id ?? null);
          }}
          {...(errors.supplier ? { error: errors.supplier } : {})}
        />
        <FormField
          label={t('po.form.expectedDate')}
          type="date"
          value={expectedDate}
          onChange={(event) => {
            setExpectedDate(event.target.value);
          }}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-ink">{t('po.form.lines')}</h3>
          {errors.lines ? <span className="text-xs text-danger-600">{errors.lines}</span> : null}
        </div>

        {lines.map((line) => (
          <div key={line.key} className="grid items-end gap-3 sm:grid-cols-[1fr_7rem_8rem_auto]">
            <ArticlePicker
              value={line.article?.id ?? null}
              onChange={(article) => {
                patchLine(line.key, { article, unitId: article?.baseUnitId ?? null });
              }}
            />
            <FormField
              label={t('po.form.qty')}
              type="number"
              min="0"
              step="0.001"
              inputMode="decimal"
              value={line.qty}
              onChange={(event) => {
                patchLine(line.key, { qty: event.target.value });
              }}
            />
            <Select
              label={t('po.form.unit')}
              options={units.options.map((unit) => ({ value: unit.id, label: unit.code }))}
              value={line.unitId ?? ''}
              onChange={(event) => {
                patchLine(line.key, { unitId: event.target.value || null });
              }}
            />
            <Button
              variant="ghost"
              type="button"
              aria-label={t('po.form.removeLine')}
              onClick={() => {
                setLines((current) =>
                  current.length === 1 ? [emptyLine()] : current.filter((l) => l.key !== line.key),
                );
              }}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          </div>
        ))}

        <Button
          variant="secondary"
          size="sm"
          type="button"
          icon={<Plus className="size-4" />}
          onClick={() => {
            setLines((current) => [...current, emptyLine()]);
          }}
        >
          {t('po.form.addLine')}
        </Button>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink" htmlFor="po-note">
          {t('po.form.note')}
        </label>
        <Textarea
          id="po-note"
          rows={2}
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onCancel}>
          {t('po.detail.back')}
        </Button>
        <Button type="button" loading={busy} onClick={submit}>
          {t('po.form.save')}
        </Button>
      </div>
    </div>
  );
}
